import { cva, type VariantProps } from 'class-variance-authority'
import { Check, Minus, X } from 'lucide-react'
import * as React from 'react'

import { cn } from '../../utils/cn'

const dotVariants = cva('z-1 flex size-5 shrink-0 items-center justify-center rounded-full border-2', {
  variants: {
    state: {
      done: 'border-accent-green bg-accent-green',
      current: 'border-ink-900 bg-surface ring-ink-900/15 ring-3',
      pending: 'border-ink-300 bg-surface border-dashed',
      /** The user's step, but a system is doing the work: a spinning arc on a grey ring. */
      processing: 'border-ink-200 border-t-ink-900 bg-surface animate-spin motion-reduce:animate-none',
    },
    /** Overrides the done fill — a finished step is not automatically a good one. */
    tone: {
      success: '',
      danger: '',
      neutral: '',
    },
  },
  compoundVariants: [
    { state: 'done', tone: 'danger', className: 'border-error bg-error' },
    { state: 'done', tone: 'neutral', className: 'border-ink-400 bg-ink-400' },
  ],
  defaultVariants: { state: 'pending' },
})

export type TimelineStepState = NonNullable<VariantProps<typeof dotVariants>['state']>
export type TimelineStepTone = NonNullable<VariantProps<typeof dotVariants>['tone']>

const GLYPHS: Record<TimelineStepTone, typeof Check> = {
  success: Check,
  danger: X,
  neutral: Minus,
}

/** Ink, not white: white on accent-green is 2.93:1, under the 3:1 minimum for a glyph. */
function DoneGlyph({ tone }: { tone: TimelineStepTone }) {
  const Glyph = GLYPHS[tone]
  return <Glyph className="text-ink-900 size-2.5" strokeWidth={tone === 'danger' ? 3.5 : 3} />
}

// `title` is a node here, not the native div tooltip attribute.
interface TimelineStepProps extends Omit<React.ComponentProps<'div'>, 'title'> {
  title: React.ReactNode
  /** Timestamp or range, beside the title by default. */
  meta?: React.ReactNode
  /** `below` stacks the meta under the title, for a column too narrow to hold both. */
  metaPosition?: 'end' | 'below'
  description?: React.ReactNode
  state?: TimelineStepState
  tone?: TimelineStepTone
}

/**
 * One step. Anything passed as children renders as a detail card beneath the
 * description — for things that happen *during* a step rather than being steps.
 */
function TimelineStep({
  title,
  meta,
  metaPosition = 'end',
  description,
  state = 'pending',
  tone = 'success',
  className,
  children,
  ...props
}: TimelineStepProps) {
  const pending = state === 'pending'

  return (
    <div
      data-slot="timeline-step"
      data-state={state}
      className={cn('group/step relative flex gap-3.5 pb-6 last:pb-0', className)}
      {...props}
    >
      <div className="relative flex w-5 shrink-0 flex-col items-center">
        <div
          data-slot="timeline-rail"
          className={cn(
            'absolute top-5 -bottom-6 w-0.5 group-last/step:hidden',
            state === 'done' ? 'bg-accent-green' : 'bg-ink-200'
          )}
        />
        <div className={dotVariants({ state, tone })}>
          {state === 'done' && <DoneGlyph tone={tone} />}
          {state === 'current' && <span className="bg-ink-900 size-2 rounded-full" />}
        </div>
      </div>

      <div className="min-w-0 flex-1 pt-px">
        <div
          className={cn(
            'flex',
            metaPosition === 'below' ? 'flex-col gap-0.5' : 'items-baseline justify-between gap-2.5'
          )}
        >
          <span className={cn('text-sm font-bold', pending && 'text-ink-500 font-semibold')}>{title}</span>
          {meta && <span className="text-ink-400 shrink-0 font-mono text-xs">{meta}</span>}
        </div>
        {description && (
          <p className={cn('text-ink-500 mt-0.5 text-sm', pending && 'text-ink-400 italic')}>{description}</p>
        )}
        {children}
      </div>
    </div>
  )
}

/** Vertical record history — what has happened, in order, each step carrying its own state. */
function Timeline({ className, ...props }: React.ComponentProps<'div'>) {
  return <div data-slot="timeline" className={cn('flex flex-col', className)} {...props} />
}

export { Timeline, TimelineStep }
